import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toaster } from "baseui/toast";
import { 
  useGetEstate, 
  useGetEstateGroup, 
  usePostEstate, 
  usePostEstateGroup,
  usePutEstateId,
  usePutEstateGroupId,
  usePatchEstateId,
  usePatchEstateGroupId,
  useDeleteEstateId,
  useDeleteEstateGroupId
} from "../api/generated";
import type { Estate, EstateGroup } from "../api/generated";
import React from "react";
import { defaultInstance } from "../api/httpClient";

// Interface for paginated API response
interface PaginatedEstatesResponse {
  data: Estate[];
  page: number;
  total_elements: number;
  total_pages: number;
}

// Custom hook for Estate Groups with pagination and search
export const useEstateGroups = (params?: {
  page?: number;
  limit?: number;
  name?: string;
}) => {
  const queryClient = useQueryClient();
  
  const { data: estateGroupsResponse, isLoading, error } = useGetEstateGroup({
    page: params?.page || 1,
    limit: params?.limit || 100,
    ...(params?.name && params.name.trim() !== '' && { name_ilike: params.name })
  } as any);
  
  // Extract data - use server-side pagination when search is used
  // The estate_group endpoint has no order_by support, so we sort client-side.
  // localeCompare with numeric+base sensitivity handles accents, mixed case,
  // and leading numbers (e.g. "7 Mares") correctly.
  const allEstateGroups = ((estateGroupsResponse?.data as any)?.data || estateGroupsResponse?.data || [])
    .slice()
    .sort((a: EstateGroup, b: EstateGroup) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' })
    );
  
  // Use server-side pagination when search is provided, client-side otherwise  
  const isServerSide = !!(params?.name && params.name.trim() !== '');
  const page = params?.page || 1;
  const limit = params?.limit || 20;
  
  let paginatedEstateGroups, totalPages;
  
  if (isServerSide) {
    // Server-side: API handles pagination and search
    paginatedEstateGroups = allEstateGroups;
    totalPages = (estateGroupsResponse as any)?.total_pages || Math.ceil(allEstateGroups.length / limit);
  } else {
    // Client-side: Manual pagination for backwards compatibility
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    paginatedEstateGroups = allEstateGroups.slice(startIndex, endIndex);
    totalPages = Math.ceil(allEstateGroups.length / limit);
  }
  
  const createEstateGroupMutation = usePostEstateGroup({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate_group'] });
        toaster.positive("Estate Group created successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error creating estate group:', error);
        toaster.negative("Error creating Estate Group", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const updateEstateGroupMutation = usePutEstateGroupId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate_group'] });
        toaster.positive("Estate Group updated successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error updating estate group:', error);
        toaster.negative("Error updating Estate Group", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const patchEstateGroupMutation = usePatchEstateGroupId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate_group'] });
        toaster.positive("Estate Group updated successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error updating estate group:', error);
        toaster.negative("Error updating Estate Group", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const deleteEstateGroupMutation = useDeleteEstateGroupId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate_group'] });
        toaster.positive("Estate Group deleted successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error deleting estate group:', error);
        toaster.negative("Error deleting Estate Group", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  return {
    estateGroups: paginatedEstateGroups,
    allEstateGroups, // Keep all groups for form selectors
    isLoading,
    error,
    // Pagination data
    totalPages,
    currentPage: page,
    totalEstateGroups: allEstateGroups.length,
    createEstateGroup: (data: EstateGroup) => createEstateGroupMutation.mutate({ data }),
    updateEstateGroup: (id: string, data: EstateGroup) => updateEstateGroupMutation.mutate({ id, data }),
    patchEstateGroup: (id: string, data: Partial<EstateGroup>) => patchEstateGroupMutation.mutate({ id, data: data as EstateGroup }),
    deleteEstateGroup: (id: string) => deleteEstateGroupMutation.mutate({ id }),
    isCreating: createEstateGroupMutation.isPending,
    isUpdating: updateEstateGroupMutation.isPending || patchEstateGroupMutation.isPending,
    isDeleting: deleteEstateGroupMutation.isPending,
  };
};

// Custom hook for Estates with pagination, filtering and search
export const useEstates = (params?: {
  estateGroupId?: number;
  buildingId?: number;
  page?: number;
  limit?: number;
  name?: string;
}) => {
  const queryClient = useQueryClient();

  // Use server-side filtering and pagination with estate_group_id parameter
  const requestParams = {
    page: params?.page || 1,
    limit: params?.limit || 20,
    estate_group_id: params?.estateGroupId,
    ...(params?.buildingId ? { building_id: params.buildingId } : {}),
    ...(params?.name && params.name.trim() !== '' && { name_ilike: params.name })
  };

  const { data: estatesResponse, isLoading, error } = useGetEstate(requestParams as any);
  
  // Extract estates data from response
  const estates: Estate[] = Array.isArray(estatesResponse?.data) ? estatesResponse.data : [];
  
  // Extract pagination metadata from response
  const totalPages = (estatesResponse as any)?.total_pages || 1;
  const currentPage = (estatesResponse as any)?.page || params?.page || 1;
  const totalEstates = (estatesResponse as any)?.total_elements || 0;
  
  const createEstateMutation = usePostEstate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate'] });
        queryClient.invalidateQueries({ queryKey: ['estates-list'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups-expanded'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups'] });
        toaster.positive("Estate created successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error creating estate:', error);
        toaster.negative("Error creating Estate", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const updateEstateMutation = usePutEstateId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate'] });
        queryClient.invalidateQueries({ queryKey: ['estates-list'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups-expanded'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups'] });
        toaster.positive("Estate updated successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error updating estate:', error);
        toaster.negative("Error updating Estate", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const patchEstateMutation = usePatchEstateId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate'] });
        queryClient.invalidateQueries({ queryKey: ['estates-list'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups-expanded'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups'] });
        toaster.positive("Estate updated successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error updating estate:', error);
        toaster.negative("Error updating Estate", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const deleteEstateMutation = useDeleteEstateId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/estate'] });
        queryClient.invalidateQueries({ queryKey: ['estates-list'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups-expanded'] });
        queryClient.invalidateQueries({ queryKey: ['estates-with-groups'] });
        toaster.positive("Estate deleted successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error deleting estate:', error);
        toaster.negative("Error deleting Estate", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  return {
    estates,
    isLoading,
    error,
    // Pagination data
    totalPages,
    currentPage,
    totalEstates,
    createEstate: (data: Estate) => createEstateMutation.mutate({ data }),
    updateEstate: (id: string, data: Estate) => updateEstateMutation.mutate({ id, data }),
    patchEstate: (id: string, data: Partial<Estate>) => patchEstateMutation.mutate({ id, data: data as Estate }),
    deleteEstate: (id: string) => deleteEstateMutation.mutate({ id }),
    isCreating: createEstateMutation.isPending,
    isUpdating: updateEstateMutation.isPending || patchEstateMutation.isPending,
    isDeleting: deleteEstateMutation.isPending,
  };
};

const ESTATES_PAGE_SIZE = 2000;
const estatesPageUrl = (page: number) =>
  `/estate?expand=estate_group,estate_group_id&order_by=name&order_direction=ASC&limit=${ESTATES_PAGE_SIZE}&page=${page}`;

/**
 * Every estate of the tenant, page after page. SAM-1323: it read one page of 2000 of 2963, so the 963 estates
 * after "T" were missing, and with them every group whose estates sort that late (7 Mares was not a destination).
 */
export const fetchAllEstatesWithGroups = async (
  get: (url: string) => Promise<any> = (url) => defaultInstance({ method: 'GET', url }),
): Promise<any[]> => {
  const first = await get(estatesPageUrl(1));
  const rows: any[] = Array.isArray(first) ? first : first?.data ?? [];
  const totalPages = Number(first?.total_pages) || (first?.total_elements ? Math.ceil(first.total_elements / ESTATES_PAGE_SIZE) : 1);
  if (totalPages <= 1) return rows;
  const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => get(estatesPageUrl(i + 2))));
  return rest.reduce<any[]>((all, page) => all.concat(Array.isArray(page) ? page : page?.data ?? []), rows);
};

// Hook to get all estates with estate_group expansion for dropdowns
export const useEstatesWithGroups = () => {
  return useQuery({
    queryKey: ["estates-with-groups"],
    queryFn: async () => {
      try {
        return await fetchAllEstatesWithGroups();
      } catch (error) {
        console.error("Error fetching estates with groups:", error);
        throw error;
      }
    }
  });
};

// Hook to get grouped estates data for the new table view
export const useEstatesGroupedView = (params?: {
  page?: number;
  limit?: number;
  searchTerm?: string;
}) => {
  // Fetch estates with estate group information using expand
  const { data: estatesWithGroupsResponse, isLoading: isLoadingEstates, error: estatesError } = useQuery({
    queryKey: ['estates-with-groups-expanded'],
    queryFn: async () => {
      try {
        // SAM-1343: every page, not the first 1000 (the same read the destination pickers make).
        return await fetchAllEstatesWithGroups();
      } catch (error) {
        console.error('❌ Error fetching estates with groups:', error);
        console.error('❌ Error details:', (error as any).response || (error as any).message);
        throw error;
      }
    }
  });
  
  // Process the data using useMemo instead of nested useQuery
  const processedData = React.useMemo(() => {
    
    // Safely extract estates array from API response
    // The API returns data directly as an array, not wrapped in a data property
    const estates = Array.isArray(estatesWithGroupsResponse) ? estatesWithGroupsResponse : 
                   Array.isArray((estatesWithGroupsResponse as any)?.data) ? (estatesWithGroupsResponse as any).data : [];
    
    if (estates.length === 0) {
      return {
        data: [],
        totalItems: 0,
        totalPages: 1,
        currentPage: 1
      };
    }
    
    
    // Check how many estates have valid estate_group_id and estate_group
    const estatesWithGroupId = estates.filter((estate: any) => estate.estate_group_id !== null && estate.estate_group_id !== undefined);
    
    // Check estates with expanded estate_group data
    const estatesWithGroupData = estates.filter((estate: any) => estate.estate_group && estate.estate_group.name);
    
    // Log sample estate data
    if (estates.length > 0) {
    }
    
    // Group estates by estate_group_id (include all estates with estate_group data)
    const estatesByGroup = estates.reduce((acc: any, estate: any) => {
      // Use estate_group.id instead of estate_group_id if available
      const groupId = estate.estate_group?.id || estate.estate_group_id;
      // Only include estates that have estate_group data
      if (estate.estate_group && estate.estate_group.name && groupId) {
        if (!acc[groupId]) {
          acc[groupId] = [];
        }
        acc[groupId].push(estate);
      }
      return acc;
    }, {});
    
    
    // Get unique estate groups from the estates data
    const uniqueEstateGroups = new Map();
    estates.forEach((estate: any) => {
      if (estate.estate_group && estate.estate_group.id && estate.estate_group.name) {
        uniqueEstateGroups.set(estate.estate_group.id, estate.estate_group);
      }
    });
    
    const estateGroups = Array.from(uniqueEstateGroups.values());
    
    // Create grouped data structure
    const groupedData: any[] = [];
    
    estateGroups.forEach((group: any) => {
      const groupEstates = estatesByGroup[group.id] || [];
      
      
      if (groupEstates.length === 0) {
        // Estate group with no estates
        groupedData.push({
          id: `group-${group.id}`,
          type: 'group',
          estateGroupId: group.id,
          estateGroupName: group.name,
          estateName: '',
          estateId: null,
          status: '',
          estateType: '',
          principal: ''
        });
      } else {
        // Estate group with estates
        groupEstates.forEach((estate: any, index: number) => {
          groupedData.push({
            id: `estate-${estate.id}`,
            type: 'estate',
            estateGroupId: group.id,
            estateGroupName: index === 0 ? group.name : '', // Only show group name on first row
            estateName: estate.name,
            estateId: estate.id,
            status: estate.status || '',
            estateType: estate.estate_type?.name || '',
            principal: '' // TODO: Add principal data when available
          });
        });
      }
    });
    
    
    // Apply search filter if provided
    let filteredData = groupedData;
    if (params?.searchTerm && params.searchTerm.trim() !== '') {
      const searchTerm = params.searchTerm.toLowerCase();
      filteredData = groupedData.filter((item: any) => 
        item.estateGroupName.toLowerCase().includes(searchTerm) ||
        item.estateName.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply pagination
    const page = params?.page || 1;
    const limit = params?.limit || 50;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    
    const result = {
      data: paginatedData,
      totalItems: filteredData.length,
      totalPages: Math.ceil(filteredData.length / limit),
      currentPage: page
    };
    
    
    if (result.data.length === 0 && groupedData.length > 0) {
    }
    
    return result;
  }, [estatesWithGroupsResponse, params?.page, params?.limit, params?.searchTerm]);
  
  return {
    data: processedData,
    isLoading: isLoadingEstates,
    error: estatesError
  };
};

export const useEstatesList = ({
  page = 1,
  limit = 20,
  searchTerm = ''
}: {
  page?: number;
  limit?: number; 
  searchTerm?: string;
}) => {
  return useQuery({
    queryKey: ['estates-list', page, limit, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams({
        expand: 'estate_group,estate_group_id',
        limit: limit.toString(),
        order_by: 'name',
        order_direction: 'ASC',
        page: page.toString()
      });

      if (searchTerm) {
        params.append('name_ilike', searchTerm);
      }

      
      const response = await defaultInstance({
        method: 'GET',
        url: `estate?${params.toString()}`
      });
      
      // response.data is already the full paginated response {data: [...], total_elements, total_pages}
      return response.data as PaginatedEstatesResponse;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Custom hook that preserves pagination metadata by using axios directly
export const useEstatesListWithPagination = ({
  page = 1,
  limit = 20,
  searchTerm = ''
}: {
  page?: number;
  limit?: number; 
  searchTerm?: string;
}) => {
  return useQuery({
    queryKey: ['estates-list-paginated', page, limit, searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams({
        expand: 'estate_group,estate_group_id',
        limit: limit.toString(),
        order_by: 'name',
        order_direction: 'ASC',
        page: page.toString()
      });

      if (searchTerm) {
        params.append('name_ilike', searchTerm);
      }

      // Use axios directly to bypass the httpClient interceptor
      const token = localStorage.getItem('token');
      const businessData = sessionStorage.getItem('businessData');
      let apiKey = '';
      
      if (businessData) {
        try {
          const parsedData = JSON.parse(businessData);
          apiKey = parsedData.api_key;
        } catch (e) {
          console.error('Error parsing business data', e);
        }
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/crud/estate?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': apiKey,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data as PaginatedEstatesResponse;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}; 